import * as React from "react";
import { useCommonHooks } from "../../../hooks";
import { ActionCode, FunctionalDirectorEditDto } from "../../../api";
import { useCrudApi } from "../../../hooks/useCrudApi";
import { FlexFieldGroup, showSuccess, useFGFormik } from "nsitools-react";
import * as Yup from "yup";
import {
  CardFieldGroup,
  CustomFormGenerator,
  FGTranslations,
} from "../../../components";
import AgentSelect from "../../../components/selects/AgentSelect";

export interface IFunctionalDirectorDetailPanelProps {
  id: number;
}

export const FunctionalDirectorDetailPanel: React.FunctionComponent<
  IFunctionalDirectorDetailPanelProps
> = ({ id }) => {
  const { apis, t, hasAction } = useCommonHooks();

  const isEditable = React.useMemo(
    () => hasAction([ActionCode.EditFunctionalDirector]),
    [hasAction]
  );

  const { data, isFetching, validationErrors, onSave, isSaving, refetch } =
    useCrudApi({
      queryKey: ["function-director-details", id],
      fetchFn: async () => {
        if (id > 0) {
          return apis.functionalDirectors.get(id);
        }
      },
      saveFn: async (toSave: FunctionalDirectorEditDto) => {
        return apis.functionalDirectors.save(toSave);
      },
      onSaved: () => {
        showSuccess(t("FunctionalDirectorSaved"));
        refetch();
      },
      serverValidationRootKey: "Dto",
    });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        agentId: Yup.number().nullable().required(t("Required")),
      }),
    [t]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: (d) => onSave(d),
    validationSchema: validationSchema,
  });

  return (
    <FlexFieldGroup columns={2}>
      <CardFieldGroup title={t("Details")}>
        <CustomFormGenerator
          initialValues={data}
          loading={isFetching}
          onSubmit={onSave}
          saving={isSaving}
          editable={isEditable}
          editMode={isEditable}
          showDeleteButton={false}
          validationErrors={validationErrors}
          validationSchema={validationSchema}
          formik={formik}
        >
          <FGTranslations label={t("Name")} />
          <AgentSelect name="agentId" label={t("Agent")} />
        </CustomFormGenerator>
      </CardFieldGroup>
    </FlexFieldGroup>
  );
};

export default FunctionalDirectorDetailPanel;
